import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  memo,
  useMemo,
  useCallback,
} from "react";
import { createContext, useContextSelector } from "use-context-selector";
import {
  Wrapper,
  CollectionContainer,
  Content,
  Fold,
  FoldScroller,
  FoldSize,
  Screen,
  Container,
  CollectionWrapper,
  PrimaryImageWrapper,
  SecondaryWrapper,
  PLP,
  PLPContent,
  PLPHeading,
  PLPNumber,
  PLPTitle,
  FrostBackdrop,
  Headline,
  Subheading,
  PLPContentContainer,
  CenterWrapper,
  CenterImage,
  ArchiveTile,
  ArchiveContent,
  ArchiveContentContainer,
  PLPImage,
  Link,
} from "../styles/components/PLP/Collection";
import { OverlayContainer } from "../styles/components/PLP/Global";
import PLPNavigation from "./PLP-Navigation";
import { BREAKPOINTS } from "../styles/variables/Breakpoints";
import gsap from "./Animations";
import PDPLanding from "./PDP-Landing";
import { COLOURS } from "../styles/variables/Colors";
import LandingNavigation from "./Landing-Navigation";

const HoverContext = createContext();

export const useHover = () => useContext(HoverContext);

const HoverProvider = ({ children }) => {
  const [hoveredKey, setHoveredKey] = useState(null);

  const handleMouseEnter = useCallback(key => {
    setHoveredKey(key);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredKey(null);
  }, []);

  const value = useMemo(
    () => ({ hoveredKey, handleMouseEnter, handleMouseLeave }),
    [hoveredKey, handleMouseEnter, handleMouseLeave]
  );

  return (
    <HoverContext.Provider value={value}>{children}</HoverContext.Provider>
  );
};

const CollectionScroll = memo(({ collection, activatePDP }) => {
  const hoveredKey = useContextSelector(
    HoverContext,
    context => context?.hoveredKey
  );
  const handleMouseEnter = useContextSelector(
    HoverContext,
    context => context?.handleMouseEnter
  );
  const handleMouseLeave = useContextSelector(
    HoverContext,
    context => context?.handleMouseLeave
  );

  const { aboutContent, aboutHeading, plpContents } = useMemo(() => {
    let aboutContentTemp = null;
    let aboutHeadingTemp = null;
    const plpContentsTemp = [];

    Object.values(collection).forEach(plpContent => {
      if (plpContent.key === "gant--about-section") {
        aboutContentTemp = plpContent;
        aboutHeadingTemp = plpContent.content.find(
          item => item.moduleTitle === "about-content"
        );
      } else {
        const plpImages = plpContent.content.filter(
          item => item.moduleTitle === "plp-primary-images"
        );
        const plpHeadlines = plpContent.content.filter(
          item => item.moduleTitle === "plp-title"
        );
        const plpParagraphs = plpContent.content.filter(
          item => item.moduleTitle === "plp-id"
        );
        const plpDescriptionHeading = plpContent.content.filter(
          item => item.moduleTitle === "plp-description-heading"
        );
        const plpDescription = plpContent.content.filter(
          item => item.moduleTitle === "plp-description"
        );
        const plpJson = plpContent.content.find(
          item => item.id && item.id.startsWith("DatoCmsJsonInput")
        );
        const pdpContent = plpContent.content.filter(
          item => item.key === "gant--pdp"
        );

        plpContentsTemp.push({
          key: plpContent.key,
          plpPrimaryImage: plpImages[0]?.images[0],
          plpSecondaryImage: plpImages[0]?.images[1],
          plpHeadlines,
          plpParagraphs,
          plpDescriptionHeading,
          plpDescription,
          jsonStyles: plpJson ? JSON.parse(plpJson.json) : null,
          pdpContent,
        });
      }
    });

    return {
      aboutContent: aboutContentTemp,
      aboutHeading: aboutHeadingTemp,
      plpContents: plpContentsTemp,
    };
  }, [collection]);

  return (
    <CollectionWrapper className="horizontal-scroll">
      {plpContents.map(
        (
          {
            key,
            plpPrimaryImage,
            plpSecondaryImage,
            plpHeadlines,
            plpParagraphs,
            jsonStyles,
          },
          index
        ) => {
          const compositeKey = `${key}-${index}`;
          const isActive = hoveredKey === compositeKey;
          return (
            <PLP
              key={compositeKey}
              id={index}
              $margin={key === 0}
              className="plp"
              onClick={() =>
                activatePDP
                  ? activatePDP(index, true, plpContents[index])
                  : null
              }
            >
              {plpPrimaryImage && (
                <PrimaryImageWrapper>
                  <PLPImage
                    src={plpPrimaryImage.image.fluid.src}
                    $aspectRatio={plpPrimaryImage.image.fluid.aspectRatio}
                  />
                </PrimaryImageWrapper>
              )}
              <PLPContentContainer
                $justifyId={index}
                onMouseEnter={() => handleMouseEnter(compositeKey)}
                onMouseLeave={handleMouseLeave}
              >
                <FrostBackdrop>
                  <SecondaryWrapper
                    $isActive={isActive}
                    $plpSecondary={plpSecondaryImage?.image.fluid.src}
                    $background={!plpSecondaryImage && jsonStyles?.colour}
                  />
                  <PLPContent $isActive={isActive}>
                    <PLPHeading>
                      {plpParagraphs.map(
                        (pdpParagraph, index) =>
                          pdpParagraph.text && (
                            <PLPNumber key={index}>
                              <Subheading
                                $isActive={isActive}
                                $whiteWrap
                                dangerouslySetInnerHTML={{
                                  __html: pdpParagraph.text,
                                }}
                              />
                            </PLPNumber>
                          )
                      )}
                      {plpHeadlines.map(
                        (pdpHeadline, index) =>
                          pdpHeadline.text && (
                            <PLPTitle key={index}>
                              <Headline
                                $isActive={isActive}
                                dangerouslySetInnerHTML={{
                                  __html: pdpHeadline.text,
                                }}
                              />
                            </PLPTitle>
                          )
                      )}
                    </PLPHeading>
                  </PLPContent>
                </FrostBackdrop>
              </PLPContentContainer>
            </PLP>
          );
        }
      )}
      {aboutContent && (
        <ArchiveTile key={aboutContent.key} id="about-archive">
          <FrostBackdrop $archive>
            <ArchiveContentContainer>
              <ArchiveContent>
                <Subheading $padding>About Archive</Subheading>
                {aboutHeading && (
                  <Headline
                    dangerouslySetInnerHTML={{ __html: aboutHeading.text }}
                  />
                )}
                <Headline $margin>
                  Want to see more of what GANT is up to today? Check out their
                  current collections <Link href="https://gant.com/">here</Link>
                  .
                </Headline>
              </ArchiveContent>
            </ArchiveContentContainer>
          </FrostBackdrop>
        </ArchiveTile>
      )}
    </CollectionWrapper>
  );
});

const Collection = memo(props => {
  const [activePDP, setActivePDP] = useState({
    id: null,
    active: false,
    content: null,
    loaded: false,
  });

  const [activePLP, setActivePLP] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const [activeNavigationScroll, setActiveNavigationScroll] = useState(true);
  const wrapperRef = useRef(null);
  const screenRef = useRef(null);
  const overlayContainerRef = useRef(null);

  const navigationRef = useRef(null);
  const navigationContainerRef = useRef(null);
  const collectionContainerRef = useRef(null);

  const observerScrollRef = useRef();
  const activePDPRef = useRef(activePDP);
  const pdpLandingRef = useRef(null);

  const mm = gsap.matchMedia();

  const activatePDP = (id, active, pdps) => {
    gsap.killTweensOf(collectionContainerRef.current);

    setActivePLP(!active);
    setActivePDP({
      id: parseFloat(id),
      active: active,
      content: pdps,
      loaded: false,
    });

    if (!active) return;

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      let timeline = gsap
        .timeline({
          onStart: () => {
            collectionContainerRef.current.classList.add("scroll");
            document.body.style.overflow = "hidden";
          },
          onComplete: () => {
            setActivePLP(false);
            observerScrollRef.current.disable();
            overlayContainerRef.current.style.height = "0";
            overlayContainerRef.current.style.minHeight = "0";
            overlayContainerRef.current.style.overflow = "hidden";
            collectionContainerRef.current.scrollTop = 0;
            collectionContainerRef.current.classList.remove("scroll");
            document.body.style.overflow = "hidden";
          },
        })
        .to(
          collectionContainerRef.current,
          {
            duration: 1,
            scrollTo: pdpLandingRef.current,
          },
          0
        );
      timeline.restart();
    });

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      let timeline = gsap
        .timeline({
          onStart: () => {
            document.body.style.overflow = "hidden";
            setTimeout(() => {
              document.querySelector(
                ".landing-wrapper"
              ).style.minHeight = `${collectionContainerRef.current.offsetHeight}px`;
            }, 500);
          },
          onComplete: () => {
            document.body.style.overflow = "unset";
          },
        })
        .to(overlayContainerRef.current, {
          scaleY: 0,
          height: 1,
          minHeight: 1,
          transformOrigin: "top top",
          duration: 0.85,
        })
        .to(collectionContainerRef.current, {
          duration: 1,
          scrollTo: pdpLandingRef.current,
        });
      timeline.restart();
    });
  };

  const handleNavigation = targetIndex => {
    const horizontalScroll = wrapperRef.current.querySelectorAll(
      ".horizontal-scroll"
    );
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let targetPosition;

    for (const carousel of horizontalScroll) {
      targetPosition = carousel.querySelector(`[id='${[targetIndex]}']`);
    }
    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.to(".horizontal-scroll", {
        x: -targetPosition.offsetLeft,
        overwrite: true,
        modifiers: {
          x: x => {
            const clampedX = gsap.utils.clamp(
              -(horizontalScroll[0].offsetWidth - viewportWidth + 400),
              viewportWidth,
              parseFloat(x)
            );
            return `${clampedX}px`;
          },
        },
      });
    });
    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      gsap.to(".horizontal-scroll", {
        y: -targetPosition.offsetTop,
        overwrite: true,
        modifiers: {
          y: y => {
            const clampedY = gsap.utils.clamp(
              -(horizontalScroll[0].offsetHeight - viewportHeight + 350),
              viewportHeight,
              parseFloat(y)
            );
            return `${clampedY}px`;
          },
        },
      });
    });
  };

  const setNavigationScroll = state => {
    setActiveNavigationScroll(state);
  };

  const setNavigationScrollPosition = scrollPosition => {
    if (!activeNavigationScroll || isMobile) return;
    const navigationWidth = navigationRef.current.offsetWidth;
    const containerWidth = navigationContainerRef.current.offsetWidth;
    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.to(navigationRef.current, {
        x: `-${scrollPosition * (navigationWidth - containerWidth)}px`,
      });
    });
  };

  const disableScrollHS = () => {
    if (!activePLP) return;
    let element = document.querySelector("#hs-web");
    if (element) {
      element.style.overflow = "hidden";
    } else {
      element = document.querySelector("#hs-app");
      if (element) {
        element.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    }
  };

  const enablePLPScroll = () => {
    if (typeof window !== "undefined" && window.innerWidth >= BREAKPOINTS.sm) {
      document.body.style.overflow = "hidden";
      collectionContainerRef.current.classList.add("scroll");

      gsap
        .timeline({
          onComplete: () => {
            overlayContainerRef.current.style.overflow = "";
            document.body.style.overflow = "";
            setActivePLP(true);
            activatePDP(null, false, null);
            observerScrollRef.current.enable();
            collectionContainerRef.current.classList.remove("scroll");
          },
        })
        .to(overlayContainerRef.current, {
          height: "100vh",
          minHeight: "100vh",
          transformOrigin: "top top",
          duration: 0.85,
          ease: "power2.inOut",
          clearProps: "height,minHeight",
        })
        .to(window, {
          duration: 0.5,
          scrollTo: 0,
        });
      return;
    }
    document.body.style.overflow = "";
    setActivePLP(true);
    activatePDP(null, false, null);
    observerScrollRef.current.enable();
    collectionContainerRef.current.classList.remove("scroll");
  };

  useEffect(() => {
    wrapperRef.current.classList.toggle("fixed", !!activePLP);
  }, [activePLP]);

  useEffect(() => {
    activePDPRef.current = activePDP;
  }, [activePDP]);

  useEffect(() => {
    setIsMobile(
      typeof window !== "undefined" && window.innerWidth <= BREAKPOINTS.sm
    );
  }, []);

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    const horizontalScroll = wrapperRef.current.querySelector(
      ".horizontal-scroll"
    );

    const offsetWidth = horizontalScroll.offsetWidth;
    const offsetHeight = horizontalScroll.offsetHeight;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const startX = viewportWidth;
    const startY = viewportHeight;
    const endX = -(offsetWidth - viewportWidth + 400);
    const endY = -(offsetHeight - viewportHeight + 350);

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap.set(".horizontal-scroll", {
        x: startX,
      });
    });

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      gsap.set(".horizontal-scroll", {
        y: startY,
      });
    });

    const goToSlide = direction => {
      let currentX = gsap.getProperty(".horizontal-scroll", "x");

      disableScrollHS();

      setNavigationScrollPosition(
        Math.abs((currentX - viewportWidth) / offsetWidth)
      );

      mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
        gsap.to(".horizontal-scroll", {
          x: `${direction}`,
          ease: "power2.out",
          modifiers: {
            x: x => {
              const clampedX = gsap.utils.clamp(endX, startX, parseFloat(x));
              return `${clampedX}px`;
            },
          },
        });
      });

      mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
        gsap.to(".horizontal-scroll", {
          y: `${direction}`,
          ease: "power2.out",
          modifiers: {
            y: y => {
              const clampedY = gsap.utils.clamp(endY, startY, parseFloat(y));
              return `${clampedY}px`;
            },
          },
        });
      });
    };

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      observerScrollRef.current = gsap.ScrollTrigger.observe({
        target: screenRef.current,
        type: "wheel,scroll",
        tolerance: 30,
        onUp: () => {
          if (activePDP.active) return;
          let direction = "+=125";
          goToSlide(direction);
        },
        onDown: () => {
          if (activePDP.active) return;
          let direction = "-=125";
          goToSlide(direction);
        },
        preventDefault: true,
      });
    });

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      observerScrollRef.current = gsap.ScrollTrigger.observe({
        target: screenRef.current,
        type: "wheel,touch",
        tolerance: 25,
        onUp: () => {
          if (activePDP.active) return;
          let direction = "-=150";
          goToSlide(direction);
        },
        onDown: () => {
          if (activePDP.active) return;
          let direction = "+=150";
          goToSlide(direction);
        },
        preventDefault: true,
      });
    });

    disableScrollHS();
    console.log("loaded");

    return () => {
      if (observerScrollRef.current) {
        observerScrollRef.current.kill();
        observerScrollRef.current = null;
      }
      gsap.killTweensOf(navigationRef.current);
    };
  }, [props]);

  return (
    <HoverProvider>
      <CollectionContainer
        ref={collectionContainerRef}
        className="collection-container"
      >
        {isMobile && (
          <LandingNavigation
            collection={props}
            onNavigate={handleNavigation}
            navigationParentRef={navigationRef}
            navigationContainerRef={navigationContainerRef}
            setNavigationScroll={setNavigationScroll}
            parentContainer={overlayContainerRef}
            activePLP={activePLP}
            activePDP={activePDP.active}
            activatePDP={activatePDP}
            observerScrollRef={observerScrollRef}
          />
        )}
        <OverlayContainer
          ref={overlayContainerRef}
          $justify="center"
          $align="flex-end"
          $zIndexBoost
        >
          {!isMobile && (
            <PLPNavigation
              collection={props}
              onNavigate={handleNavigation}
              navigationParentRef={navigationRef}
              navigationContainerRef={navigationContainerRef}
              setNavigationScroll={setNavigationScroll}
              activePLP={activePLP}
            />
          )}
          <Wrapper ref={wrapperRef} $position="absolute" $active>
            <Screen ref={screenRef}>
              <Container>
                {["left", "center", "right"].map((position, index) => {
                  if (position === "center") {
                    return (
                      <CenterWrapper key={index}>
                        <CenterImage src="https://www.datocms-assets.com/40484/1710520852-gant.svg" />
                        <Fold $position={position}>
                          <FoldSize $position={position}>
                            <FoldScroller $position={position}>
                              <Content>
                                <CollectionScroll
                                  collection={props}
                                  activatePDP={activatePDP}
                                />
                              </Content>
                            </FoldScroller>
                          </FoldSize>
                        </Fold>
                      </CenterWrapper>
                    );
                  }
                  return (
                    <Fold key={index} $position={position}>
                      <FoldSize $position={position}>
                        <FoldScroller $position={position}>
                          <Content>
                            <CollectionScroll collection={props} />
                          </Content>
                        </FoldScroller>
                      </FoldSize>
                    </Fold>
                  );
                })}
              </Container>
            </Screen>
          </Wrapper>
        </OverlayContainer>
        <Wrapper
          ref={pdpLandingRef}
          $background={COLOURS.primary.white}
          $active={activePDP.active}
        >
          {activePDP.active && (
            <PDPLanding
              activePDP={activePDP.content}
              activatePDP={activatePDP}
              parentContainer={overlayContainerRef}
              enablePLPScroll={enablePLPScroll}
            />
          )}
        </Wrapper>
      </CollectionContainer>
    </HoverProvider>
  );
});

export default Collection;
